import { AccountsService } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { ToastQueue } from 'base/ui/toast';
import { randomUUID } from 'base/uuid';
import { AuthFlowDialog } from 'blocks/auth/dialog';
import { AccessLevel, BlocksService } from 'blocks/blocks_service';
import {
  canViewWireframe,
  elementTreeDfs,
  walkProject,
} from 'blocks/editor/model_helpers';
import { PresenceDecorations } from 'blocks/sync/presence_decorations';
import { loadProject } from 'blocks/sync/sync';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Page, Project, Section } from '../model';
import { createAuthDialogs } from './auth_dialog';
import { createDeleteDialog } from './delete_dialog';
import { createDuplicateDialog } from './duplicate_dialog';
import { showPaymentProblemToast } from './payment_problem_toast';
import { PresenceAvatars } from './presence';
import {
  saveMenuItem,
  saveShortcutHandler,
  SyncFatalErrorDialog,
} from './saving_ui';
import { ProjectSession } from './session';
import { createUpgradeDialogs, ShowUpgradeWallFn } from './upgrade_dialog';

export async function startSession(
  accountStore: AccountStore,
  blocksService: BlocksService,
  accountsService: AccountsService,
  analyticsNoContext: AnalyticsService,
  toastQueue: ToastQueue,
  projectToken: string,
  isLoggedIn: boolean,
): Promise<ProjectSession> {
  const {
    project,
    undo,
    flags,
    productRestrictions,
    sync,
    isNewProject,
    analyticsContext,
    locked,
    createdBy,
    createdAt,
  } = await loadProject({
    blocksService,
    projectToken,
  });
  const analytics = analyticsNoContext.withContext(() => analyticsContext);
  const sessionShared = {
    analytics,
    locked,
    project,
    projectToken,
    flags,
    createdBy,
    createdAt,
    comments: sync.comments,
    decorations: sync.decorations,
    headerRightStart: (
      <PresenceAvatars
        presence={sync.presence}
        bordered={true}
        maxAvatars={4}
        background="grey"
      />
    ),
  } satisfies Partial<ProjectSession>;

  if (flags.get('hasPaymentProblem')) {
    showPaymentProblemToast(accountsService, toastQueue);
  }

  const { accessLevel } = sync.initMsg;

  if (!sync) {
    throw new Error("can't be in edit mode without sync impl");
  }

  const unloadConfirmationDisabled = observable.box(false);
  const disableUnloadConfirmation = () =>
    sync.syncNow().then(() => unloadConfirmationDisabled.set(true));

  const planName = accountStore.member?.planName;

  const auth = isLoggedIn
    ? undefined
    : createAuthDialogs(accessLevel, disableUnloadConfirmation);
  const upgrading = isLoggedIn
    ? createUpgradeDialogs(
        { accessLevel: sync.initMsg.accessLevel, flags },
        disableUnloadConfirmation,
        sync,
        planName,
      )
    : undefined;

  const showUpgradeWall: ShowUpgradeWallFn = (feature) =>
    auth?.openAuthThenUpgradeWall() ||
    upgrading?.showUpgradeWall(feature, analytics);

  const duplicator = isLoggedIn
    ? createDuplicateDialog(
        project,
        sync.projectToken,
        blocksService,
        showUpgradeWall,
      )
    : undefined;
  const deleter =
    isLoggedIn && accessLevel >= AccessLevel.OWNER
      ? createDeleteDialog(project, sync.projectToken, blocksService)
      : undefined;

  const DialogLayer = observer(() => {
    React.useEffect(() => {
      const showUnsavedChangesToast =
        auth != null &&
        (sync.hasMadeChanges || sync.hasUnsavedChanges) &&
        hasMadeSubstantialEdits(project, sync.decorations) &&
        accessLevel >= AccessLevel.OWNER;
      if (sync.isOffline) {
        return toastQueue.addPersistent(() => ({
          label:
            'You are offline. Changes will be lost unless you reconnect soon.',
        }));
      }
      if (showUnsavedChangesToast) {
        return toastQueue.addPersistent(() => ({
          label: 'Heads up! You are editing an unsaved project.',
          action: {
            label: 'Save',
            onClick: auth!.openAuthWall,
          },
        }));
      }
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth, sync.hasMadeChanges, sync.hasUnsavedChanges, sync.isOffline]);

    return (
      <>
        {sync.fatalError && (
          <SyncFatalErrorDialog
            error={sync.fatalError}
            hasUnsavedChanges={sync.hasUnsavedChanges}
          />
        )}
        {auth && <auth.DialogLayer />}
        {upgrading?.getDialog()}
        {duplicator?.getDialog()}
        {deleter?.getDialog()}
      </>
    );
  });

  // Ensure all elements in the project have unique IDs.
  if (accessLevel >= AccessLevel.OWNER) {
    undo.doPermanently(() => {
      const seenIds = new Set<string>();
      const seenSections = new Set<Section>();
      walkProject(
        project,
        ({ type, node }) => {
          if (type !== 'section') return;

          // Ensure we only visit global sections once, otherwise we'd encounter
          // their elements multiple times and mistakenly treat all their IDs as
          // duplicates.
          if (seenSections.has(node)) return;
          seenSections.add(node);

          if (!node.element.current) return;

          for (const el of elementTreeDfs(node.element.current)) {
            // Give the element an ID if it's missing one, or if that ID is
            // already being used by another element.
            if (!el.id) {
              el.id = randomUUID();
            } else if (seenIds.has(el.id)) {
              console.warn(`Replacing duplicate element ID: ${el.id}`);
              el.id = randomUUID();
            }
            seenIds.add(el.id);
          }
        },
        { visitElements: false },
      );
    });
  }

  return {
    ...sessionShared,
    undo,
    accessLevel,
    beforeUnloadHandler: (e) => {
      if (unloadConfirmationDisabled.get()) return;
      if (!sync.hasUnsavedChanges && (isLoggedIn || !sync.hasMadeChanges))
        return;
      e.preventDefault();
      e.returnValue = '';
      sync.syncNow();
    },
    isNewProject,
    canExportComponents:
      flags.get('canExportComponents') && accessLevel >= AccessLevel.OWNER,
    canViewWireframe: (page) =>
      canViewWireframe(productRestrictions, project, page),
    canViewAllWireframes: flags.get('canExportComponents'),
    saveShortcutHandler:
      accessLevel >= AccessLevel.OWNER
        ? () => saveShortcutHandler(sync, auth?.openAuthWall, toastQueue)
        : () => {},
    saveMenuItem:
      accessLevel >= AccessLevel.OWNER
        ? () => saveMenuItem(sync, auth?.openAuthWall)
        : undefined,
    duplicateMenuItem: duplicator?.menuItem,
    deleteMenuItem: deleter?.menuItem,
    headerRightEnd:
      // Don't show `upgrading.headerButton` if session is locked because the header
      // will show a custom upgrade button ("Unlock project") instead.
      auth?.headerButton || (!sessionShared.locked && upgrading?.headerButton),
    dialogLayer: <DialogLayer />,
    showAuthWall: auth?.openAuthWall,
    InlineAuthDialog: isLoggedIn
      ? undefined
      : (props) => (
          <AuthFlowDialog
            {...props}
            accountsService={accountsService}
            disableUnloadConfirmation={disableUnloadConfirmation}
          />
        ),
    showUpgradeWall: showUpgradeWall,
    productRestrictions,
    isRelumeStaff: accountStore.member?.email.endsWith('@relume.io') ?? false,
  };
}

/**
 * Design intention: used to determine whether to show "unsaved changes" toast is shown:
 *  - NOT SHOWN if the user just edited metadata (e.g. sitemap prompt)
 *  - NOT SHOWN if they're still running generate sitemap
 *  - SHOWN after they first generate the sitemap
 *  - SHOWN if they manually add sections
 **/
function hasMadeSubstantialEdits(
  project: Project,
  presenceDecorations: PresenceDecorations,
) {
  if (project.spaces.length) {
    return true;
  }

  function hasFinishedPage(page: Page) {
    if (
      page.sections.length &&
      !presenceDecorations.arePageSectionsPending(page)
    ) {
      return true;
    }
    return page.subPages.some(hasFinishedPage);
  }
  return hasFinishedPage(project.homePage);
}
